import { useEffect, useState } from "react";
import { StarIcon } from "@heroicons/react/24/solid";
import { useI18n } from "~/i18n/i18n";

const DISMISS_KEY = "prizo_rate_dismissed";

/**
 * Floating "Rate us on Shopify" star button shown on every authenticated app
 * screen (rendered from Layout). Auto-opens once on load, closes only via the X
 * (or after clicking through to rate), and the dismissal persists in
 * localStorage so it doesn't keep popping back up.
 */
export default function RateUsFab() {
  const { t, locale } = useI18n();
  const isRTL = locale === "he";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) !== "1") setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} z-40 flex flex-col ${isRTL ? "items-start" : "items-end"} gap-3`}>
      {open && (
        <div
          className="relative w-[300px] rounded-cardlg p-6 shadow-lift text-center"
          style={{ background: "linear-gradient(150deg,#fff7ed,#fdeede)" }}
        >
          <button
            onClick={dismiss}
            aria-label={t("rateUs.dismiss")}
            className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} text-[#b45309]/60 hover:text-[#b45309]`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <div className="flex justify-center gap-1 mb-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <StarIcon key={i} className="w-6 h-6 text-[#f59e0b] drop-shadow-[0_1px_2px_rgba(124,58,18,.25)]" />
            ))}
          </div>
          <div className="font-display text-[18px] font-bold text-[#7c3a12]">{t("rateUs.title")}</div>
          <p className="mt-2 mb-4 text-[13.5px] leading-[1.55] text-[#9a6b3f] font-semibold">
            {t("rateUs.body")}
          </p>
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-[13px] text-white text-[14px] font-extrabold shadow-[0_8px_18px_rgba(245,158,11,.35)] transition-transform hover:-translate-y-0.5"
            style={{ background: "linear-gradient(140deg,#fbbf24,#f59e0b)" }}
          >
            <StarIcon className="w-[18px] h-[18px]" />
            {t("rateUs.cta")}
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen(true)}
        aria-label={t("rateUs.ariaOpen")}
        title={t("rateUs.cta")}
        className={`${isRTL ? "self-start" : "self-end"} w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-lift ring-4 ring-white animate-floaty hover:scale-105 transition-transform`}
        style={{ background: "linear-gradient(140deg,#fbbf24,#f59e0b)" }}
      >
        <StarIcon className="w-8 h-8 text-white drop-shadow-[0_2px_3px_rgba(124,58,18,.4)]" />
      </button>
    </div>
  );
}
